/**
 * ============================================================================
 * useRiskDetailQuery — Phase 7 frontend (Quality Risk Management Module).
 * Owner: Phase 7 — risk-svc.
 * Purpose: Fetch one RiskDetail (including score-history + review timelines)
 *          for the RiskEntry FMEA editor page.
 *
 * URS rows: RSK-009 (detail), RSK-001 (history), RSK-008 (review history).
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type { RiskCategory, RiskLevel, RiskStatus, TreatmentKind } from './useRisksQuery';

export interface RiskScoreSnapshot {
  scoreId: string;
  severityScore: number;
  occurrenceScore: number;
  detectabilityScore: number;
  rpn: number;
  riskLevel: RiskLevel;
  scoredAtUtc: string;
  scoredByUserId: string;
  rationale: string;
}

export interface RiskReviewSnapshot {
  reviewId: string;
  reviewedAtUtc: string;
  reviewerUserId: string;
  outcome: 'UNCHANGED' | 'INCREASED' | 'DECREASED' | 'CLOSED' | 'ACCEPTED';
  rationale: string;
}

export interface RiskDetail {
  riskId: string;
  riskNumber: string;
  title: string;
  description: string;
  riskCategory: RiskCategory;
  processScope: string;
  relatedSupplierId: string | null;
  relatedEquipmentId: string | null;
  relatedProductRef: string | null;
  riskOwnerUserId: string;
  severityScore: number;
  occurrenceScore: number;
  detectabilityScore: number;
  rpn: number;
  riskLevel: RiskLevel;
  treatmentKind: TreatmentKind;
  treatmentPlan: string | null;
  residualRpn: number | null;
  status: RiskStatus;
  nextReviewDueUtc: string | null;
  lastReviewedUtc: string | null;
  acceptSignaturePrimaryId: string | null;
  acceptSignatureSecondaryId: string | null;
  createdAtUtc: string;
  createdByUserId: string;
  scoreHistory: RiskScoreSnapshot[];
  reviewHistory: RiskReviewSnapshot[];
}

export function useRiskDetailQuery(riskId: string | undefined): UseQueryResult<RiskDetail> {
  return useQuery<RiskDetail>({
    queryKey: ['risk', riskId],
    queryFn: () => apiFetch<RiskDetail>(`/api/v1/risks/${encodeURIComponent(riskId!)}`),
    enabled: !!riskId,
    staleTime: 10_000,
  });
}
